import { useState } from "react";
import { DatePicker } from "@/components/ui/date-picker";
import { Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CalendarDays, Download, FileJson, FileSpreadsheet, FileText, TrendingUp, TriangleAlert, WalletCards } from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { useReports } from "@/hooks/useReports";
import { exportReport, reportBundle } from "@/utils/exports";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
// A period the owner has just switched to reads `[]` on every series until the fetch
// lands. Gating the three panels on length alone printed "Nothing settled yet today"
// and "No items sold in this period" over a request still in flight — a closed day
// and an unanswered one told the same story, on the screen used to sign the day off.
import { LoadingState } from "@/components/ui/StateBoundaries";
// The local `₹${toLocaleString}` this replaces had no fraction digits at all, so
// the chart tooltip said ₹1,240 for a figure the EOD panel beside it printed as
// ₹1,240.50. One formatter, one answer (§4.2, §10.2).
import { MONEY_GRAND_TOTAL, MONEY_LINE, inr } from "@/lib/format";

const formats = [["pdf","PDF",FileText],["xlsx","Excel",FileSpreadsheet],["csv","CSV",FileSpreadsheet],["json","JSON",FileJson],["xml","XML",FileText]];
// DESIGN.md §2: chart colors come from the theme tokens so dark mode follows —
// recharts needs literal paint values, hence the hsl(var(--…)) form.
const SLICE_COLOURS = [
  "hsl(var(--primary))", "hsl(var(--chart-2))", "hsl(var(--chart-4))",
  "hsl(var(--chart-5))", "hsl(var(--destructive))", "hsl(var(--chart-3))",
];

// Every value in `stats` is a pre-formatted string — three of the five are money
// that useReports already put through inr(), two are counts — so the strip cannot
// tell them apart and sizes them all as one row of figures rather than reaching
// for a money slot it cannot honestly pick.
const STRIP_VALUE = "text-lede font-semibold tabular-nums";

export default function ReportsPanelV2() {
  const { token, settings } = usePOS();
  const [period, setPeriod] = useState("Today");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [lastExport, setLastExport] = useState(null);

  const dayStart = settings?.business_day_start || settings?.businessDayStart || "06:00";
  const { eod, stats, hourlySeries, paymentSeries, topCategories, error, loading, rangeIncomplete, range } = useReports(
    token,
    period,
    { dayStart, from: customFrom, to: customTo }
  );

  // The range the figures below were read for: one date for a single day, "from to
  // to" otherwise. It printed today's date under Week and Month (2026-10-24).
  const rangeLabel = !range ? "—" : range.fromDate === range.toDate ? range.fromDate : `${range.fromDate} to ${range.toDate}`;

  const runExport = (format, filename = `restoyaar-sales-${period.toLowerCase()}`) => {
    const bundle = reportBundle({ period, stats: stats.map(s => [s.label, s.value, "", null]), sales: hourlySeries, payments: paymentSeries, sellers: topCategories });
    const receipt = exportReport(format, bundle, filename);
    setLastExport(receipt);
    toast.success(`${format.toUpperCase()} report exported`);
  };

  return <div className="space-y-loose min-w-0 w-full" data-testid="reports-panel">
    {/*
      * LIVE OPERATIONS over "Sales & day close" over a date line, on a panel that is
      * already inside a tab labelled "Sales overview" inside a page the topbar calls
      * Reports. What is left is the controls and the one fact that qualifies every
      * figure below: closed orders only, for this date.
      */}
    <header className="flex flex-col xl:flex-row xl:items-center justify-between gap-snug">
      <div className="flex flex-wrap items-baseline gap-snug min-w-0">
        <p className="text-meta text-muted-foreground">
          Closed orders only · <span className="tabular-nums" data-testid="report-range-label">{rangeLabel}</span>
        </p>
        {lastExport && (
          <span className="px-snug py-hair bg-success-subtle text-success text-micro font-bold rounded-md border border-success/40" data-testid="report-export-receipt" aria-live="polite">
            Ready · {lastExport.filename} · <span className="tabular-nums">{lastExport.bytes.toLocaleString()}</span> bytes
          </span>
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-snug xl:items-center shrink-0">
        <div className="flex flex-wrap items-center gap-snug">
          <div className="flex bg-muted/50 p-hair rounded-lg border" role="group">
            {["Today","Week","Month","Custom"].map(value => (
              <button
                className={`px-base py-hair text-meta font-medium rounded-md transition-colors ${period === value ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                onClick={() => setPeriod(value)}
                key={value}
                data-testid={`report-period-${value.toLowerCase()}-button`}
              >
                {value}
              </button>
            ))}
          </div>

          {period === "Custom" && (
            <div className="flex items-center gap-tight" data-testid="report-custom-range">
              <DatePicker
                value={customFrom}
                max={customTo || undefined}
                onChange={setCustomFrom}
                ariaLabel="From"
                testId="report-custom-from-date-input"
              />
              <span className="text-meta text-muted-foreground font-semibold">to</span>
              <DatePicker
                value={customTo}
                min={customFrom || undefined}
                onChange={setCustomTo}
                ariaLabel="To"
                testId="report-custom-to-date-input"
              />
            </div>
          )}
        </div>

        <div className="flex items-center" data-testid="report-export-actions">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="default"
                className="h-9 px-snug text-meta font-medium shadow-md"
                data-testid="export-report-button"
              >
                <Download className="w-3.5 h-3.5 mr-tight" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[12rem]">
              {formats.map(([format, label, Icon]) => (
                <DropdownMenuItem
                  key={format}
                  onSelect={() => runExport(format)}
                  data-testid={`export-report-${format}-button`}
                  className="text-body font-medium cursor-pointer"
                >
                  <Icon className="w-3.5 h-3.5 mr-tight" /> {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>

    {error && (
      <div className="bg-destructive/10 text-destructive border border-destructive/20 p-base rounded-lg flex items-start gap-snug" role="alert" data-testid="reports-error">
        <TriangleAlert className="w-4 h-4 shrink-0 mt-hair"/><span className="text-body font-semibold">{error}</span>
      </div>
    )}

    {/*
      * Five rounded cards, each with a 40px tinted chip holding an icon that repeated
      * the label beside it — a rupee glyph next to the words "Gross sales" — spent
      * about 120px of height on five numbers an owner reads in one glance. One
      * bordered strip, five label/value cells, no chips (§2.2, §21).
      */}
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-border border rounded-lg overflow-hidden">
      {stats.map(({ label, value }) => (
        <div className="p-base bg-card flex flex-col gap-hair min-w-0" key={label} data-testid={`report-stat-${label.toLowerCase().replaceAll(" ","-")}`}>
          <small className="text-meta font-medium text-muted-foreground truncate">{label}</small>
          <strong className={`${STRIP_VALUE} text-foreground truncate`}>{value}</strong>
        </div>
      ))}
      {!stats.length && !loading && (
        <div className="p-base bg-card flex flex-col gap-hair min-w-0 col-span-full" data-testid="report-stat-empty">
          <small className="text-meta font-medium text-muted-foreground">No closed orders</small>
          <strong className={`${STRIP_VALUE} text-muted-foreground`}>—</strong>
        </div>
      )}
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-loose items-start">
      <div className="space-y-loose min-w-0 w-full">
        <section className="bg-card border rounded-xl p-base sm:p-loose shadow-sm flex flex-col min-w-0 w-full" data-testid="hourly-sales-chart">
          <header className="flex justify-between items-baseline gap-snug mb-base">
            <div className="flex flex-col">
              <h3 className="text-title font-semibold text-foreground">Sales through the day</h3>
              <p className="text-meta text-muted-foreground">Collection by service hour{period === "Today" ? ", today" : ` (${period})`}</p>
            </div>
            <CalendarDays className="w-4 h-4 text-muted-foreground shrink-0" />
          </header>
          {hourlySeries.length ? (
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={hourlySeries} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="salesFillV2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} dy={10} />
                  <YAxis hide />
                  <Tooltip
                    formatter={value => [inr(value), "Sales"]}
                    contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)", fontSize: "12px", fontWeight: "bold" }}
                  />
                  <Area type="monotone" dataKey="sales" stroke="hsl(var(--primary))" strokeWidth={3} fill="url(#salesFillV2)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : loading ? (
            <div className="h-[250px] flex items-center justify-center border-2 border-dashed rounded-xl">
              <LoadingState message="Reading sales…" testId="hourly-sales-loading" />
            </div>
          ) : rangeIncomplete ? (
            <div className="h-[250px] flex items-center justify-center border-2 border-dashed rounded-xl">
              <p className="text-body text-muted-foreground">Select a date range to view sales.</p>
            </div>
          ) : (
            <div className="h-[250px] flex items-center justify-center border-2 border-dashed rounded-xl">
              <p className="text-body text-muted-foreground" data-testid="hourly-sales-empty">Nothing settled in this period.</p>
            </div>
          )}
        </section>

        <section className="bg-card border rounded-xl p-base sm:p-loose shadow-sm flex flex-col min-w-0 w-full" data-testid="payment-breakdown-chart">
          <header className="flex justify-between items-baseline gap-snug mb-base">
            <div className="flex flex-col">
              <small className="text-meta font-medium text-warning">{period === "Today" ? "Today only" : period}</small>
              <h3 className="text-title font-semibold text-foreground">Payment modes</h3>
              <p className="text-meta text-muted-foreground">Collection split · <span className="tabular-nums" data-testid="payment-range-label">{rangeLabel}</span></p>
            </div>
            <WalletCards className="w-4 h-4 text-muted-foreground shrink-0" />
          </header>
          {paymentSeries.length ? (
            <div className="flex flex-col sm:flex-row items-center gap-loose">
              <div className="w-48 h-48 sm:w-56 sm:h-56 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={paymentSeries} dataKey="value" innerRadius={60} outerRadius={90} paddingAngle={2} stroke="none">
                      {paymentSeries.map((entry, index) => <Cell key={entry.name} fill={SLICE_COLOURS[index % SLICE_COLOURS.length]}/>)}
                    </Pie>
                    <Tooltip
                      formatter={value => [inr(value), ""]}
                      contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)", fontSize: "12px", fontWeight: "bold" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-col gap-snug flex-1 w-full min-w-0">
                {paymentSeries.map((item, index) => (
                  <div className="flex items-baseline justify-between gap-snug text-body" key={item.name}>
                    <div className="flex items-center gap-snug min-w-0">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: SLICE_COLOURS[index % SLICE_COLOURS.length] }}></span>
                      <span className="font-medium text-muted-foreground truncate">{item.name}</span>
                    </div>
                    <strong className={`${MONEY_LINE} text-foreground shrink-0`}>{inr(item.value)}</strong>
                  </div>
                ))}
              </div>
            </div>
          ) : loading ? (
            <div className="h-[210px] flex items-center justify-center border-2 border-dashed rounded-xl">
              <LoadingState message="Reading collection…" testId="payment-breakdown-loading" />
            </div>
          ) : rangeIncomplete ? (
            <div className="h-[210px] flex items-center justify-center border-2 border-dashed rounded-xl">
              <p className="text-body text-muted-foreground">Select a date range to view payment modes.</p>
            </div>
          ) : (
            <div className="h-[210px] flex items-center justify-center border-2 border-dashed rounded-xl">
              <p className="text-body text-muted-foreground" data-testid="payment-breakdown-empty">No settled payments in this period.</p>
            </div>
          )}
        </section>
      </div>

      <div className="space-y-loose min-w-0 w-full">
        <section className="bg-card border rounded-xl p-base sm:p-loose shadow-sm flex flex-col min-w-0 w-full" data-testid="end-of-day-panel">
          <header className="mb-base">
            <small className="text-meta font-medium text-primary block">Today only · audit &amp; close</small>
            <h3 className="text-title font-semibold text-foreground">End of day</h3>
            <p className="text-meta text-muted-foreground">
              Review collection totals before closing the business date. These four figures are for {eod?.date || "today"} whichever period is selected above.
            </p>
          </header>
          <div className="bg-muted/30 border rounded-lg divide-y mb-snug">
            <div className="flex justify-between items-baseline gap-snug px-base py-snug">
              <span className="text-body text-muted-foreground">Taxable value</span>
              <b className={MONEY_LINE}>{inr(eod?.taxable_value)}</b>
            </div>
            <div className="flex justify-between items-baseline gap-snug px-base py-snug">
              <span className="text-body text-muted-foreground">CGST + SGST</span>
              <b className={MONEY_LINE}>{inr((Number(eod?.cgst) || 0) + (Number(eod?.sgst) || 0))}</b>
            </div>
            <div className="flex justify-between items-baseline gap-snug px-base py-snug">
              <span className="text-body text-muted-foreground">Discounts given</span>
              <div className="text-right">
                <b className={`${MONEY_LINE} block`}>{inr(eod?.discount_total)}</b>
                <span className="text-meta text-muted-foreground tabular-nums">{eod?.discount_count ?? 0} discounts</span>
              </div>
            </div>
            <div className="flex justify-between items-baseline gap-snug px-base py-snug bg-primary/5">
              <span className="text-body font-bold text-foreground">Collected</span>
              <b className={`${MONEY_GRAND_TOTAL} text-success`}>{inr(eod?.revenue)}</b>
            </div>
          </div>
          <p className="text-meta text-muted-foreground" data-testid="eod-cash-note">
            Cash drawer reconciliation is on the Staff &amp; shifts screen.
          </p>
        </section>

        <section className="bg-card border rounded-xl p-base sm:p-loose shadow-sm flex flex-col min-w-0 w-full" data-testid="top-sellers-table">
          <header className="flex justify-between items-baseline gap-snug mb-base">
            <div className="flex flex-col min-w-0">
              <h3 className="text-title font-semibold text-foreground">Top categories</h3>
              <p className="text-meta text-muted-foreground">Ranked by revenue</p>
            </div>
            <TrendingUp className="w-4 h-4 text-muted-foreground shrink-0" />
          </header>
          {topCategories.length ? (
            <div className="border rounded-lg overflow-x-auto scroll-contain-x" data-testid="top-categories-table-container">
              <table className="w-full text-left border-collapse min-w-[300px]" data-testid="top-categories-table">
                <thead>
                  <tr className="border-b bg-muted/40 text-meta text-muted-foreground">
                    <th className="px-base py-snug font-medium">Name</th>
                    <th className="px-base py-snug font-medium text-right">Qty</th>
                    <th className="px-base py-snug font-medium text-right">Total Amount (with tax)</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {topCategories.map((item, index) => (
                    <tr
                      className="hover:bg-muted/30 transition-colors"
                      key={item.name || item.bucket}
                      data-testid={`top-seller-${index}`}
                    >
                      <td className="px-base py-snug text-body font-semibold text-foreground truncate max-w-[160px]">
                        {item.name || item.bucket}
                      </td>
                      <td className="px-base py-snug text-body text-muted-foreground tabular-nums text-right">
                        {item.qty}
                      </td>
                      <td className={`px-base py-snug ${MONEY_LINE} tabular-nums text-right text-foreground`}>
                        {inr(item.amountWithTax ?? item.revenue_with_tax ?? item.revenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : loading ? (
            <LoadingState message="Ranking categories…" testId="top-sellers-loading" />
          ) : rangeIncomplete ? (
            <p className="text-body text-muted-foreground">Select a date range to rank categories.</p>
          ) : (
            <p className="text-body text-muted-foreground" data-testid="top-sellers-empty">No items sold in this period.</p>
          )}
        </section>
      </div>
    </div>
  </div>;
}
